import { useQuery as useDataQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';

import { Dealer as Advertiser } from '../../../types/Dealer';
import { QueryRegistryKey } from './QueryRegistry';
import { useAuthToken } from './AuthToken';
import { useQueryRegistry } from './QueryRegistry';
import UrlUtils from '../../../utils/UrlUtils';

interface QueryCriteria {
    advertiserId?: number;
    advertiser?: Advertiser | null;
    mediaType?: string;
    apiToken: string;
    range: {
        startDate: number;
        endDate: number;
        period: string;
    };
}

const useQuery = (queryRegistryKey: QueryRegistryKey, criteria: QueryCriteria) => {
    const { data } = useAuthToken(criteria.advertiserId);
    const { clickstreamXAuthToken, clickstreamUrl, duplicateClickstreamXAuthToken = false } = data || {};

    const { buildQuery } = useQueryRegistry();

    return useDataQuery({
        queryKey: ['services/trialfire/analytics/query', queryRegistryKey, criteria],
        queryFn: async () => {
            const query = buildQuery(queryRegistryKey, {
                apiToken: criteria.apiToken,
                isDuplicateApiToken: duplicateClickstreamXAuthToken,
                range: criteria.range,
                browserTimezone: moment.tz.guess(),
                mediaType: criteria.mediaType,
                domain: UrlUtils.strip(criteria?.advertiser?.url as string),
                advertiserId: criteria.advertiserId,
            });

            if (!query) {
                throw new Error('Failed initialize query.');
            }

            const payload = query.build();

            if (Object.entries(payload).length === 0) {
                return null;
            }

            const response = await fetch(query.buildUrl(clickstreamUrl ?? ''), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': clickstreamXAuthToken ?? '',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch data.');
            }

            return response.json();
        },
        enabled:
            !!clickstreamXAuthToken &&
            !!clickstreamUrl &&
            !!criteria.apiToken &&
            !!criteria.range.startDate &&
            !!criteria.range.endDate,
        refetchOnWindowFocus: false,
    });
};

export type { QueryCriteria };
export { useQuery };
